import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Head from 'next/head';

import PackageHeader from 'components/PackageHeader';
import Navigation from 'components/packages/HeaderPackage/Navigation';
import Wrapper from 'components/packages/HeaderPackage/Wrapper';

import TitleLogo from 'components/TitleLogo';
import ShowLeadBackground from './Background';

import './styles.themed.scss';

const mapStateToProps = ({ front: { curation } }) => ({
  seoTitle: curation.seoTitle,
});

/**
 * Title component renders the package title inside a PackageHeader.
 *
 * @param {object} props - Component props.
 * @param {string} props.title - The title of the package.
 * @param {number} props.packageIndex - The index of the package in the context.
 */
const Title = ({
  title,
  packageIndex,
}) => {
  if (!title) {
    return null;
  }

  return (
    <PackageHeader
      className="show-lead__show--name"
      packageIndex={packageIndex}
    >
      {title}
    </PackageHeader>
  );
};

/**
 * Logo component renders the package logo inside a PackageHeader.
 *
 * @param {object} props - Component props.
 * @param {string} props.logoUrl - The URL of the package logo.
 * @param {number} props.packageIndex - The index of the package in the context.
 * @param {string} props.seoTitle - The SEO title to use as alt text if title is missing.
 * @param {string} props.title - The title of the package, used for alt text.
 */
const Logo = ({
  logoUrl,
  packageIndex,
  seoTitle,
  title,
}) => (
  <PackageHeader
    packageIndex={packageIndex}
    className="show-lead__show--name"
  >
    <TitleLogo logoUrl={logoUrl} alt={title || seoTitle} />
  </PackageHeader>
);

/**
 * @param {object} props - Component props.
 * @param {import('../../../lib/graphqlTypeDefs.mjs').Package} props.content - Curation package data.
 * @param {string} [props.pkgClassName='pkg lead showLead'] - Additional class names for the wrapper.
 * @param {string} [props.seoTitle] - SEO title for alt text fallback.
 */
const ShowLead = ({
  content: {
    id,
    metadata: {
      description,
      imageUrl,
      imageUrlMobile,
      logoUrl,
      navigation,
      title,
    },
    packageIndex,
    context: {
      isFluidWidthPage,
    },
  },
  pkgClassName = 'pkg lead showLead',
  seoTitle,
}) => {
  // Filter to remove empty links
  const filteredNavigation = useMemo(() => (
    navigation?.filter((item) => item?.linkUrl) || []
  ), [navigation]);

  return (
    <>
      <Head>
        {imageUrl && <link rel="preload" as="image" href={imageUrl} />}
        {imageUrlMobile && <link rel="preload" as="image" href={imageUrlMobile} />}
        {logoUrl && <link rel="preload" as="image" href={logoUrl} />}
      </Head>
      <Wrapper
        id={id}
        className={classNames(pkgClassName, 'show-lead')}
      >
        <div className="show-lead__wrapper relative">
          { !isFluidWidthPage
            ? (
              <ShowLeadBackground
                imageUrl={imageUrl}
                imageUrlMobile={imageUrlMobile}
              />
            )
            : null }
          <div className={classNames('show-lead__content', 'layout-grid-container')}>
            <div className={classNames('show-lead__meta--wrap', 'z-1')}>
              <div className={classNames('show-lead__meta-data', 'layout-grid-item')}>
                {logoUrl && (
                  <Logo
                    seoTitle={seoTitle}
                    logoUrl={logoUrl}
                    title={title}
                    packageIndex={packageIndex}
                  />
                )}
                {!logoUrl && <Title title={title} packageIndex={packageIndex} />}
                {description && (
                  <h3 className="show-lead__air-time">
                    {description}
                  </h3>
                )}
                <Navigation
                  navigation={filteredNavigation}
                  styleOverride={{
                    linkListWrapper: 'show-lead__link-list--wrapper',
                    linkList: 'show-lead__link-list',
                    linkListItem: 'show-lead__link-list--item',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    </>
  );
};

export default connect(mapStateToProps)(ShowLead);
